import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Clock, MapPin, Calendar as CalIcon } from "lucide-react";
import { getClassBySlug, CONTACT } from "@/data/classes";

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function RegistrationSuccess() {
  const location = useLocation();
  const reg = location.state;
  if (!reg) return <Navigate to="/schedule" replace />;

  const c = getClassBySlug(reg.class_slug);

  return (
    <div className="bg-[#f9fafb] min-h-screen py-16">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="registration-success">
          <CheckCircle2 className="w-16 h-16 text-[#d90429] mx-auto" />
          <div className="font-mono-label text-[#d90429] mt-6">Confirmed</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-black text-[#0a0a0a]">
            You&apos;re registered.
          </h1>
          <p className="mt-4 text-gray-700 text-lg">
            Thanks, {reg.first_name}! Your spot in <strong>{c?.name}</strong> is locked in.
          </p>
          <div className="mt-8 inline-block bg-[#f9fafb] border border-gray-200 rounded-md px-6 py-4">
            <div className="font-mono-label text-gray-500 mb-1">Confirmation Code</div>
            <div className="font-display text-2xl font-black tracking-wider text-[#0a0a0a]" data-testid="confirmation-code">
              {reg.confirmation_code}
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
            <div className="p-5 bg-[#f9fafb] border border-gray-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <CalIcon className="w-4 h-4 text-[#d90429]" />
                <span className="font-mono-label text-gray-500">Date</span>
              </div>
              <div className="font-bold text-[#0a0a0a]">{formatDate(reg.session_date)}</div>
            </div>
            <div className="p-5 bg-[#f9fafb] border border-gray-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-[#d90429]" />
                <span className="font-mono-label text-gray-500">Time</span>
              </div>
              <div className="font-bold text-[#0a0a0a]">{reg.start_time} – {reg.end_time}</div>
            </div>
            <div className="p-5 bg-[#f9fafb] border border-gray-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-[#d90429]" />
                <span className="font-mono-label text-gray-500">Location</span>
              </div>
              <div className="font-bold text-[#0a0a0a]">{reg.location}</div>
            </div>
          </div>

          <p className="mt-10 text-gray-600 text-sm max-w-lg mx-auto">
            Please save your confirmation code. We&apos;ll be in touch before class with
            arrival details. Questions? Call{" "}
            <a href={CONTACT.phoneHref} className="text-[#d90429] font-bold">
              {CONTACT.phone}
            </a>
            .
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              to="/schedule"
              data-testid="success-book-another"
              className="inline-flex items-center gap-2 rounded-md bg-[#0a0a0a] px-6 py-3 text-sm font-bold text-white hover:bg-[#d90429] transition-colors"
            >
              Book Another Class
            </Link>
            <Link
              to="/"
              data-testid="success-home-link"
              className="inline-flex items-center gap-2 rounded-md border-2 border-[#0a0a0a] px-6 py-3 text-sm font-bold text-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
            >
              Back to Home <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
